import useSWR from 'swr';
import { KeyedMutator, SWRConfiguration } from 'swr/dist/types';

import { FetchError } from '../classes/FetchError';
import { BaseApiResponseState } from '../interfaces/BaseAPIResponseState';
import { ErrorResponseJSONInterface } from '../interfaces/ErrorResponseJSONInterface';
import { loginRedirect } from '../utils/loginRedirect';

export interface DataState<Data> extends BaseApiResponseState<Data> {
  mutate: KeyedMutator<Data>;
}

export async function fetcher<T>(
  url: string,
  skipRedirectOn401?: boolean,
  dangerouslyUsePOSTWithPayload?: string,
  headers?: string,
): Promise<T | undefined> {
  const response: Response = await (async () => {
    try {
      if (dangerouslyUsePOSTWithPayload) {
        return await fetch(url, {
          credentials: 'include',
          method: 'POST',
          body: dangerouslyUsePOSTWithPayload,
          headers: {
            'Content-Type': 'application/json',
            ...(headers && JSON.parse(headers)),
          },
        });
      } else {
        return await fetch(url, {
          credentials: 'include',
          method: 'GET',
          ...(headers && { headers: JSON.parse(headers) }),
        });
      }
    } catch (e: unknown) {
      // The only error which can be thrown here is a TypeError,
      // SOURCE: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
      const err = e as { message?: string };
      if (err.message) {
        throw new FetchError(err.message);
      } else {
        throw new FetchError('unknown fetch error');
      }
    }
  })();
  if (response.ok) {
    try {
      return (await response.json()) as T;
    } catch (e: unknown) {
      const err = e as { message?: string };
      if (err.message) {
        throw new FetchError(err.message);
      } else {
        throw new FetchError('error while parsing response json');
      }
    }
  }
  // If we got a 401, redirect to login page
  if (response.status === 401 && !skipRedirectOn401) {
    loginRedirect();
    return undefined;
  }
  // there can be an error while parsing JSON
  const errorData = await (async () => {
    try {
      // WARNING: there is no guarantee that this JSON has the right format
      return (await response.json()) as ErrorResponseJSONInterface;
    } catch (e: unknown) {
      return undefined;
    }
  })();
  throw new FetchError(response.statusText, response.status, errorData);
}

export interface UseFetchArgs<Data> {
  // Allow passing a null url to support conditional fetching
  // https://swr.vercel.app/docs/conditional-fetching#conditional
  url: string | null;
  // We allow `any` here since most deserializer functions should specify the correct type
  // in their signatures.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deserializer?: (data: any) => Data;
  // Pass custom SWR options
  // https://swr.vercel.app/docs/options#options
  swrConfig?: SWRConfiguration;
  // By default we redirect to the login page on any 401 response; set this to true to disable this
  skipRedirectOn401?: boolean;
  // While useSWR is only meant to be used for GET requests, we support non-mutating POST requests in special
  // cases, e.g. if the payload of the request is too big to fit into query params.
  dangerouslyUsePOSTWithPayload?: unknown;
  // Custom headers
  headers?: { [key: string]: string };
}

function defaultDeserializer<Data>(data: unknown): Data {
  // By default the data is returned as-is
  return data as Data;
}

/**
 * A wrapper around useSWR.
 *
 * https://swr.vercel.app/
 */
export function useFetch<Data>(args: UseFetchArgs<Data>): DataState<Data> {
  let data: Data | undefined = undefined;

  // These should match the order of the args to `fetcher` above
  // If url is null - prevent the request by passing null key
  // as described here https://swr.vercel.app/docs/conditional-fetching
  const key =
    args.url !== null
      ? [
          args.url,
          args.skipRedirectOn401,
          JSON.stringify(args.dangerouslyUsePOSTWithPayload),
          JSON.stringify(args.headers),
        ]
      : null;

  const { data: rawData, error, mutate } = useSWR(key, fetcher, args.swrConfig);

  if (rawData && !error) {
    if (args.deserializer) {
      data = args.deserializer(rawData);
    } else {
      data = defaultDeserializer<Data>(rawData);
    }
  }

  return {
    data,
    error,
    isLoading: key !== null ? !data && !error : false,
    // Used for updating the local data immediately instead of waiting for a re-fetch
    // See https://swr.vercel.app/docs/mutation#bound-mutate
    mutate,
  };
}
